import React, { useState } from 'react';
import ADataGrid from '../../../../common/data-grid/ADataGrid';
import { AGridColDef } from '../../../../common/data-grid/ADataGridColDef';
import { Tooltip } from '@mui/material';
import { useGetExamChargesContext } from '../../context';
import AButton from '../../../../common/form-fields-mui/AButton';
import ApplyWaiveOffDialog from '../dialogs/ApplyWaiveOffDialog';
import APopover from '../../../../common/a-popover';
import IconButtonWrapper from '../../../../common/form-fields-mui/IconButtonWrapper';
import { BsThreeDotsVertical } from "react-icons/bs";
import TableMenuActions from './TableMenuActions';
import { printReceipt, REACT_ICONS_STYLE } from '../../../../masters/studentFinancials/paymentCollection/manage-receipts/constants';
import { useNavigateToGivenPath } from '../../../../edpOps/studentDetails/paymentSummary/service';
import { CourseDetailsPopupById, EyeIcon } from '../../../studentCharges/common';
import CustomChip from '../../../../common/customChip/CustomChip';
import { statusChip } from '../../../../edpOps/studentDetails/paymentSummary/StudentFeeSummary';
import TestResultDialog from '../dialogs/TestResultDialog';
import ALoader from '../../../../common/a-loader';
import { getFormatedDate } from '../../../concessionApproval/common';
import { formattedPrice } from '../../../collectPayment/utils';

const examModeKeyDispValMapping = {
  "OFF" : "Offline",
  "ONL" : "Online"
}

const ExamsListing = () => {

  const {
    studentChargesList,
    selectionModel,
    setSelectionModel,
    setSelectedCharges,
    fetchExamChargesData,
  } = useGetExamChargesContext();

  const navigateToGivenUrl = useNavigateToGivenPath();

  const [applyWaiveOffDialogInfo, setApplyWaiveOffDialogInfo] = useState({});
  const [testResultDialogInfo, setTestResultDialogInfo] = useState({ isOpen: false, exam: {} });
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);

  const rows = studentChargesList['OTHER_CHARGES'];

  const onSelectionModelChange = newSelectionModel => {
    if (newSelectionModel?.length > 1) return;
    setSelectionModel(newSelectionModel);
    setSelectedCharges(prev => ({
      ...prev,
      OTHER_CHARGES: rows?.filter(row => newSelectionModel.includes(row?.id))
    }))
  }

  const isRowSelectable = params => {
    const { approvalStatus, id, isApproved, waveOffAmount } = params?.row;

    if (['PAID', 'Cancelled']?.includes(approvalStatus)) return false;

    if(!isApproved && waveOffAmount !== 0) return false; //the exam charge is pending for approval

    if (selectionModel?.length > 0 && !(selectionModel?.includes(id))) return false; // allow to select one row only

    return true;
  }

  const MENU_ITEMS = [
    {
      icon: null,
      text: "Print Receipt",
      handleClick: async (rowData) => {
        setIsGeneratingReceipt(true);

        await printReceipt({
          receipts: [rowData],
        });
        setIsGeneratingReceipt(false);
      },
      isDisabled: (rowData) => !(rowData?.receiptId)
    },
    {
      icon: null,
      text: "View Result",
      handleClick: (rowData) => {
        setTestResultDialogInfo({ isOpen: true, exam: rowData })
      },
      isDisabled: (rowData) => !(rowData?.resultExists)
    },
    {
      icon: null,
      text: "View Application",
      handleClick: (rowData) => {
        navigateToGivenUrl(`admin/studentCharges/details/${rowData?.applicationId}/view`)

      },
      isDisabled: (rowData) => !(rowData?.applicationId)
    },
    {
      icon: null,
      text: "Manage Receipt",
      handleClick: (rowData) => {
        const {
          acknowledgementId,
          applicationId,
          businessArea,
          psid,
          receiptId,
        } = rowData;

        navigateToGivenUrl(`/admin/paymentCollection/viewReceipt/${psid}?acknowledgementId=${acknowledgementId === "N/A" ? "" : acknowledgementId
          }&businessArea=${encodeURIComponent(
            businessArea
          )}&applicationId=${applicationId ? encodeURIComponent(applicationId) : ""}&receiptId=${encodeURIComponent(receiptId)}`)

      },
      isDisabled: (rowData) => !(rowData?.acknowledgementId)
    },
  ];




  const columns = [

    new AGridColDef("rollNumber", "Roll No")
      .setMinWidth(130)
      .renderCellComponent((params) => (
        <div>{params?.value?.trim() || "-"}</div>
      )),

    new AGridColDef("examTimeCode", "Exam ID")
      .setMinWidth(100)
      .setFlex(0.5)
      .renderCellComponent((params) => (
        <>
          <div>{params?.value?.trim() || "-"}</div>
          <Tooltip title={params?.value}>
            <span className='ml-1'><EyeIcon /></span>
          </Tooltip>
        </>
      )),

    new AGridColDef("courseId", "Course ID")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <div>
          <span>{params.value}</span>&nbsp;
          <CourseDetailsPopupById courseId={params?.value} />
        </div>
      )),

    new AGridColDef("term", "Term")
      .setMinWidth(80)
      .renderCellComponent((params) => (
          <span>{params?.value || "-"}</span>
      )),

    new AGridColDef("businessAreaValue", "Center")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <Tooltip title={params?.value}>
          <div className="ellipsis-text-container">{params?.value ?? "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("examDate", "Exam Date")
      .setMinWidth(150)
      .renderCellComponent((params) =>
        <div>{getFormatedDate(params?.value, false, 'DD-MM-YYYY')}</div>
      ),

    new AGridColDef("studentClass", "Class")
      .setMinWidth(80)
      .renderCellComponent((params) => (
        <Tooltip title={params?.value}>
          <div className="ellipsis-text-container">{params?.value || params?.row?.studentClass || "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("mode", "Mode")
      .setMinWidth(80)
      .renderCellComponent((params) => {
        const dispVal = examModeKeyDispValMapping[params?.value]
        return(
        <Tooltip title={dispVal}>
          <div className="ellipsis-text-container">{dispVal?? "-"}</div>
        </Tooltip>
      )}),

    new AGridColDef("paymentMethod", "PaymentMode")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <Tooltip title={params?.value}>
          <div className="ellipsis-text-container">{params?.value ?? "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("finalPayable", "Total Amount")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <Tooltip title={params?.value}>
          <div className="ellipsis-text-container">{formattedPrice(params?.value) ?? "-"}</div>
        </Tooltip>
      )),

    new AGridColDef("sfTransactionId", "Transaction ID")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <Tooltip title={params?.value}>
          <div className="ellipsis-text-container">{params?.value ?? "-"}</div>
        </Tooltip>
      )),

    //   new AGridColDef("attended", "Attended")
    //   .setMinWidth(100)
    //   .renderCellComponent((params) => (
    //     <Tooltip title={params?.value}>
    //       <div className="ellipsis-text-container">{params?.value ?? "-"}</div>
    //     </Tooltip>
    //   )),

    new AGridColDef("waveOffAmount", "Waive Off")
      .setMinWidth(130)
      .renderCellComponent((params) => {

        const { waveOffAmount, approvalStatus, approverUserId, approvalStatusUpdatedOn, id , isApproved} = params?.row;


        if (!isApproved && !waveOffAmount) return ( //when the charge is either new or rejected
          <AButton
            variant='link'
            size='xs'
            disabled={(approvalStatus === 'PAID' || selectionModel?.includes(id))}
            onClick={() => {
              setApplyWaiveOffDialogInfo({ isOpen: true, examCharge: params?.row })
            }}
          >
            Waive Off
          </AButton>
        )

        if(isApproved) return (
          <div className="ellipsis-text-container">
            <span className='font-weight-bold'>Waived</span>
            <Tooltip title={`Waived Off By ${approverUserId} on ${getFormatedDate(approvalStatusUpdatedOn)}`}>
              <span className='ml-1'><EyeIcon /></span>
            </Tooltip>
          </div>
        )

        if (!isApproved && waveOffAmount !== 0) return ( //when the charge is pending for approval
          <span className='font-weight-bold'>Approval Pending</span>
        )

      }),

    new AGridColDef("status", "Status")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <CustomChip {...statusChip[params?.value]} />
      )),

    new AGridColDef("actions", "Actions")
      .setMinWidth(100)
      .renderCellComponent((params) => (
        <APopover
          buttonComponent={(onClick, styles) => (
            <Tooltip title="More Actions">
              <span onClick={onClick}>
                <IconButtonWrapper>
                  <BsThreeDotsVertical style={REACT_ICONS_STYLE} />
                </IconButtonWrapper>
              </span>
            </Tooltip>
          )}
          menuComponent={(closeForm) => (
            <TableMenuActions
              handleClose={closeForm}
              menuItems={MENU_ITEMS}
              rowData={params?.row}
            />
          )}
          sx={{
            "& .MuiPaper-root": {
              border: `1px solid #E2E2E2`,
              boxShadow: "0px 3px 4px 0px #00000014 !important",
              borderRadius: "8px",
            },
          }}
        />
      )),
  ]

  return (
    <>
      {isGeneratingReceipt && <ALoader position={"fixed"} />}
      <ADataGrid
        rows={rows}
        columns={columns}
        rowId={(row) => row.id}
        hidePagination={true}
        loading={studentChargesList?.loading}
        // emptyRowPlaceholder={() => (
        //   <NoReceiptsFound isCentralizedAdm={isCentralizedAdm} />
        // )}
        checkboxSelection
        serverPagination={false}
        isRowSelectable={isRowSelectable}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        disableSelectionOnClick
        hideFooterSelectedRowCount={true}
        height={"40%"}
        disableMultipleRowSelection={true}
      />

      <ApplyWaiveOffDialog
        applyWaiveOffDialogInfo={applyWaiveOffDialogInfo}
        setApplyWaiveOffDialogInfo={setApplyWaiveOffDialogInfo}
        key={JSON.stringify(applyWaiveOffDialogInfo)}
        fetchExamChargesData={fetchExamChargesData}
      />

      <TestResultDialog
        testResultDialogInfo={testResultDialogInfo}
        setTestResultDialogInfo={setTestResultDialogInfo}
        key={JSON.stringify(testResultDialogInfo)}
        fetchExamChargesData={fetchExamChargesData}
      />
    </>
  );
}

export default ExamsListing;